/**
 * Static content for the Eduspace "Guidelines" page (/eduspace-guidelines).
 *
 * GUIDELINES is transcribed verbatim from Eduspace_Booking_Guidelines_Final.pdf --
 * this is policy text, so edit it only against an updated PDF.
 *
 * TUTORIALS holds the six screen recordings. The MP4s are NOT in this repo (~150MB);
 * upload them to the CDN and paste each URL into `videoUrl` below. A tutorial with an
 * empty `videoUrl` renders as "Coming soon" and is not clickable, so the page is safe
 * to ship before the uploads land.
 */

export const TUTORIALS = [
    {
        id: "access",
        step: 1,
        title: "Accessing the Eduspace platform",
        blurb: "Sign in and find your way around the Eduspace workspace.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/xA1S5eh1nWWkqjMRHENq3g",
        poster: "",
    },
    {
        id: "book-dashboard",
        step: 2,
        title: "Booking from the Dashboard",
        blurb: "Create a classroom booking straight from the calendar grid.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/yrBgGBsl10tTMEKgsFoVcA",
        poster: "",
    },
    {
        id: "book-availability",
        step: 3,
        title: "Booking a Classroom through the Availability Section",
        blurb: "Search free rooms by date, time and capacity, then reserve one.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/BziN0E4GLmPrEHR5qvVziw",
        poster: "",
    },
    {
        id: "edit-booking",
        step: 4,
        title: "Editing a Booking",
        blurb: "Change the room, date, time or purpose of an existing booking.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/537Sp8YrbTUrRx2tnaq7aQ",
        poster: "",
    },
    {
        id: "copy-move",
        step: 5,
        title: "Copy, Paste & Move Bookings",
        blurb: "Duplicate a booking across slots and drag it to a new time.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/ojzjEGP7o12IOEp-duebsQ",
        poster: "",
    },
    {
        id: "undo-redo",
        step: 6,
        title: "Undo and Redo",
        blurb: "Step back and forward through changes on the schedule board.",
        duration: "",
        videoUrl: "https://files.unionstack.in/f/QTmT20t7PERw9CuYdTO6cA",
        poster: "",
    },
];

export const GUIDELINES = [
    {
        id: "booking-window",
        step: 1,
        title: "Booking Window",
        paras: [
            "Classroom bookings for each month will open one month in advance. Users cannot place requests beyond the active booking window. Requests beyond the permitted booking window will only be considered in exceptional cases, subject to approval by the Operations Team.",
        ],
    },
    {
        id: "booking-details",
        step: 2,
        title: "Booking Details",
        paras: [
            "Please mention the booking purpose, School/Department, classroom(s), date, and time. Kindly verify all details carefully before submitting your request.",
            'If the booking is for an event, please select the "Event" booking type to ensure it is appropriately highlighted on the dashboard for better visibility and operational planning.',
        ],
    },
    {
        id: "booking-priority",
        step: 3,
        title: "Booking Priority",
        paras: [],
        list: [
            "Academic Sessions & Examinations",
            "Admissions Activities",
            "Student Activities",
            "Institutional Events",
            "External Events",
        ],
    },
    {
        id: "booking-modifications",
        step: 4,
        title: "Booking Modifications",
        paras: [
            "Users may edit or cancel requests before approval. Confirmed bookings may also be modified, subject to classroom availability and operational feasibility.",
        ],
    },
    {
        id: "bulk-booking",
        step: 5,
        title: "Bulk Booking",
        paras: [
            "Where this functionality is enabled, users may create multiple classroom bookings within a single request.",
        ],
    },
    {
        id: "classroom-allocation",
        step: 6,
        title: "Classroom Allocation",
        paras: [
            "Classroom allocation is based on the optimal utilization of campus resources and operational feasibility. The Operations Team may modify the allocated venue, if required.",
            "Users are requested to book only the resources genuinely required and avoid unnecessary venue bookings.",
        ],
    },
];

/**
 * Case-insensitive search across both lists. Returns the full lists for an empty query
 * so callers can render `searchHelp(q)` unconditionally.
 */
export const searchHelp = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return { tutorials: TUTORIALS, guidelines: GUIDELINES };

    const matches = (...parts) =>
        parts.filter(Boolean).join(" ").toLowerCase().includes(q);

    return {
        tutorials: TUTORIALS.filter((t) => matches(t.title, t.blurb)),
        guidelines: GUIDELINES.filter((g) =>
            matches(g.title, ...(g.paras || []), ...(g.list || []))
        ),
    };
};
